// EDEN · quiet draft autosave (Module D polish). A debounced keystroke becomes one save; a calm
// muted indicator says so; backgrounding the phone flushes the tail with a keepalive fetch.
// Offline is never an alarm — it reads "will save when you're back online" and retries itself.
//
// Staleness guard: pages re-render by innerHTML swaps with no teardown hook, so every callback
// first checks the indicator is still in the document. A detached indicator means this instance
// belongs to a previous render — it must go quiet (dispose), never save over a newer screen.

import { isNetworkError } from "./offline";
import { t } from "./i18n";

export interface Autosave {
  /** Call on every input — schedules a debounced save. */
  schedule(): void;
  /** Cancel any pending tick and wait out an in-flight save (call before an explicit Save/Submit). */
  settle(): Promise<void>;
  /** Remove window/document listeners. Safe to call twice. */
  dispose(): void;
}

const DELAY_MS = 2000;

export function initAutosave(opts: {
  indicator: HTMLElement;
  /** Save the form's CURRENT state. Return false when there was nothing to save yet. */
  save: (keepalive: boolean) => Promise<boolean>;
}): Autosave {
  const { indicator, save } = opts;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let dirty = false;
  let inflight: Promise<void> | null = null;
  let disposed = false;

  const show = (text: string): void => {
    indicator.textContent = text;
    // When the indicator lives inside the redesign's .sync-chip (plan-new capture), reveal the
    // chip only when it has something to say — otherwise a lone dot would sit in the topbar.
    indicator.closest(".sync-chip")?.classList.toggle("active", text !== "");
  };

  function stale(): boolean {
    if (!disposed && !indicator.isConnected) dispose();
    return disposed;
  }

  async function runOnce(keepalive: boolean): Promise<void> {
    dirty = false;
    const chip = indicator.closest(".sync-chip");
    chip?.classList.add("saving"); // yellow dot while a save is in flight (sync-chip only)
    show(t("autosave.saving"));
    try {
      const did = await save(keepalive);
      if (stale()) return;
      show(did ? t("autosave.saved") : "");
    } catch (err) {
      if (stale()) return;
      dirty = true; // keep it pending — the online listener / the next input retries
      show(isNetworkError(err) ? t("autosave.offline") : t("autosave.retry"));
    } finally {
      chip?.classList.remove("saving");
    }
  }

  function run(keepalive = false): Promise<void> {
    if (stale()) return Promise.resolve();
    if (inflight) return inflight; // `dirty` makes the landing save reschedule
    inflight = runOnce(keepalive).finally(() => {
      inflight = null;
      if (dirty && !disposed) schedule(); // changes arrived while saving — go again
    });
    return inflight;
  }

  function schedule(): void {
    if (stale()) return;
    dirty = true;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      void run();
    }, DELAY_MS);
  }

  async function settle(): Promise<void> {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    if (inflight) await inflight;
  }

  const onHide = (): void => {
    // Phone backgrounded mid-typing: push the tail NOW; keepalive lets it outlive the tab freeze.
    if (document.visibilityState === "hidden" && (dirty || timer)) {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      void run(true);
    }
  };
  const onOnline = (): void => {
    if (dirty && !timer) void run();
  };

  document.addEventListener("visibilitychange", onHide);
  window.addEventListener("online", onOnline);

  function dispose(): void {
    disposed = true;
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    document.removeEventListener("visibilitychange", onHide);
    window.removeEventListener("online", onOnline);
  }

  return { schedule, settle, dispose };
}
